/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { loadDistributionTagAtom } from '@/atoms/distributionTagAtoms';
import ForceUpdate from '@/components/force-update/force-update';
import SignInModal from '@/components/modal/sign-in-modal';
import { FullPageLoader } from '@/components/ui/full-page-loader';
import { ModalBlurOverlay } from '@/components/ui/modal-blur-overlay';
import { hasCompletedDictationOnboarding } from '@/features/dictation/dictate-onboarding-flow/utils/dictateOnboardingPersistence';
import {
  DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS,
  useDesktopBootRoutingConnectivity,
} from '@/hooks/desktop/useDesktopConnectivity';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useKinde } from '@/hooks/useKinde';
import { clearPersistedModels } from '@/lib/authActions';
import { getSafeRedirectUrl } from '@/lib/navigation-allowlist';
import { AuthProvider, getPersistedAuthProvider } from '@/models/authModel';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { type RootState } from '@/store';
import { useDesktopAuthSafe } from '@/tauri/auth0/desktop-auth-context';
import { cachedIdentityBridge } from '@/tauri/offline-recording/identity';
import { isTauri } from '@/tauri/utils';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { isJWTExpired } from '@/utils/auth/isJWTExpired';
import {
  boundedPersistFlush,
  createBootNavigator,
  shouldRedirectDesktopTokenProvider,
} from '@/utils/bootNavigation';
import { isDictateBundle, isKeycloak } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { getPersistor } from '@rematch/persist';
import { useSetAtom } from 'jotai';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

const messages = defineMessages({
  pageTitle: {
    defaultMessage: 'Log in to Heidi | Your AI Care Partner for Modern Clinical Practice',
    id: 'WWYAjL1GQW',
    description: 'HTML document title for the home sign-in page',
  },
  pageDescription: {
    defaultMessage:
      'Log in to Heidi, your AI care partner. Heidi automates clinical documentation so you can focus on patient care.',
    id: 'ziCjBZBCC0',
    description: 'Meta description for SEO and social previews on the home sign-in page',
  },
});

const PERSIST_FLUSH_TIMEOUT_MS = 1000;

const Home = () => {
  const intl = useIntl();
  const kindeAuth = useKinde();
  const router = useRouter();
  const { token, authProvider } = useSelector((rootState: RootState) => rootState.auth);
  const { getToken } = kindeAuth;
  // Desktop cold start: the Rust session restores (context below) BEFORE the
  // auth cache's mirror-write lands the token in Redux — with a virgin webview
  // profile (no redux-persist rehydration) the Redux `token` above can stay
  // empty for the whole boot. The redirect gate must also accept the desktop
  // context session or an authenticated boot strands on the sign-in page.
  // Sentinel (session: null) everywhere outside the Tauri provider — web unaffected.
  const { session: desktopSession } = useDesktopAuthSafe();
  const desktopAccessToken = desktopSession?.accessToken ?? null;
  // Single source of truth for desktop connectivity (routing view). While
  // `isDesktopConnectivityPending`, the boot redirect must not fire and the
  // loader must hold. `isDesktopRoutingOffline` is a confirmed offline reading
  // or a stuck-pending that hit the bounded fail-safe.
  //
  // DSK-536: this reading is NOT trusted to pick the default any more — see
  // `shouldDefaultToOnboarding` below. It is kept as one more way to reach the
  // safe answer, never as the thing that has to be right.
  const {
    isPending: isDesktopConnectivityPending,
    isRoutingOffline: isDesktopRoutingOffline,
  } = useDesktopBootRoutingConnectivity();

  // DSK-536 — the boot default is `/scribe`, and `/scribe/new-user` is only
  // reached on a POSITIVE assertion that onboarding is needed.
  //
  // Five previous fixes all tried to make the "am I offline?" answer smarter so
  // the offline branch could flip the default. That answer cannot be made
  // reliable here: `classifyDesktopConnectivity` can only say `offline` from the
  // Rust probe (armed exclusively by `useIsOfflineRecordingEnabled()`, and Rust
  // deliberately sets no boot-time target after the Jul-2026 prod LB 503
  // incident — `src-tauri/src/lib.rs`) or from `navigator.onLine === false`
  // (inert on macOS, where WKWebView pins it `true` —
  // `src/hooks/desktop/useNavigatorOnline.ts`). Flag off + macOS + genuinely
  // offline hits neither, so the classifier answers `online` and every
  // offline-keyed guard here is a no-op. So this stopped depending on the
  // answer: the WRONG answer is now harmless because the fallback is the safe
  // route either way.
  //
  // The positive assertion is the cached desktop identity, which never runs
  // through the classifier. Rust snapshots it into the keychain at login from
  // the access token, and `CachedIdentity::from_access_token`
  // (`tauri-plugin-auth0/src/secure_storage.rs`) returns `None` unless the token
  // carries BOTH a user ref id and a region — and a region only exists once the
  // country picker on `/scribe/new-user` has run. So:
  //   identity present -> onboarding is already done; `/scribe` is correct.
  //   identity `null`  -> no online login with a region has ever completed on
  //                       this device, which IS the brand-new desktop signup
  //                       (desktop Auth0 login returns to `/` with no
  //                       `post_login_redirect_url`, so this default is how a
  //                       fresh desktop signup reaches onboarding at all).
  // The read is a local keychain call with no network, so it is equally valid
  // online and offline — which is what makes this branch-agnostic: it holds
  // whether the classifier answered `online` immediately or the 8s fail-safe
  // fired. Off-desktop the bridge resolves `null` synchronously-enough that web
  // keeps today's `/scribe/new-user` default unchanged.
  // `undefined` = read still in flight; the redirect effect holds on it.
  const [hasCachedDesktopIdentity, setHasCachedDesktopIdentity] = useState<
    boolean | undefined
  >(() => (isTauri() ? undefined : false));
  useEffect(() => {
    if (!isTauri()) return;
    let active = true;
    // `cachedIdentityBridge.read()` is a native IPC on the boot path and the
    // redirect gate holds while it is `undefined`. A rejection is handled
    // below, but a HANG is not a rejection: a stalled `invoke` never settles
    // either branch, so without a ceiling the boot loader waits forever — the
    // same never-resolving-signal failure class this ticket exists to kill.
    // Same ceiling as the connectivity fail-safe, started at mount so the two
    // run concurrently rather than stacking.
    // `active` doubles as the settled-once latch, so whichever of the three
    // outcomes (resolve / reject / ceiling) lands first wins and the others are
    // no-ops. The pending timer is cleared on unmount below.
    const settle = (hasIdentity: boolean) => {
      if (!active) return;
      active = false;
      setHasCachedDesktopIdentity(hasIdentity);
    };
    const readCeiling = setTimeout(() => {
      if (!active) return;
      logger.warn(
        `[index] Cached desktop identity read did not settle within ${DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS}ms; falling back to the onboarding default`
      );
      settle(false);
    }, DESKTOP_BOOT_CONNECTIVITY_TIMEOUT_MS);
    void cachedIdentityBridge
      .read()
      .then((identity) => {
        // POSITIVE, not merely non-`null`. What makes "already onboarded" true
        // is a `userId` AND a `region` — a region only exists once the country
        // picker on `/scribe/new-user` has run. `identity !== null` would read
        // any other non-null value as identity-present: `undefined` from a stub
        // or a bridge that stopped resolving `null`, or a partial payload from
        // a future plugin change. That is the one direction that SKIPS
        // onboarding rather than repeating it, so it is the one this read must
        // never reach by accident.
        settle(Boolean(identity?.userId && identity.region));
      })
      .catch(() => {
        // A failed keychain read must not strand the gate on `undefined` —
        // that would hold the boot loader forever. Degrade to "no identity",
        // i.e. exactly the pre-DSK-536 default, so an unreadable keychain is
        // never WORSE than today. The bridge reports the failure once with a
        // bounded name, so this caller only handles the fallback state.
        settle(false);
      });
    return () => {
      active = false;
      clearTimeout(readCeiling);
    };
  }, []);
  const isDesktopIdentityPending = hasCachedDesktopIdentity === undefined;

  // `/scribe/new-user` is now the exception, not the fallback. Every term below
  // must be POSITIVELY true for it:
  // - not on-prem: Keycloak/the deployment provisions users, so the cloud
  //   new-user self-correction request can leave the Tauri root page holding its
  //   loader forever;
  // - not a confirmed/timed-out offline boot (kept as belt-and-braces — it is no
  //   longer load-bearing, see above);
  // - a RESOLVED `false` from the cached-identity read. `undefined` (still
  //   reading) is not `false`; the redirect is held rather than guessed.
  const shouldDefaultToOnboarding =
    !isKeycloak && !isDesktopRoutingOffline && hasCachedDesktopIdentity === false;
  const postLoginRedirectUrl =
    getSafeRedirectUrl(router.query.post_login_redirect_url) ??
    (shouldDefaultToOnboarding ? '/scribe/new-user' : '/scribe');

  // Unified auth status (Kinde + Auth0)
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();
  const flagsReady = usePosthogFlagsReady();

  // Read authProvider directly from localStorage to avoid Redux rehydration timing issues.
  const persistedAuthProvider = getPersistedAuthProvider();
  const isAuth0Desktop =
    authProvider === AuthProvider.AUTH0_DESKTOP ||
    persistedAuthProvider === AuthProvider.AUTH0_DESKTOP;

  // Hydrate the persisted distribution marker on launch (read-only; Rust owns
  // the stamp). Consumed by the resolved `isDictateExperience` gate elsewhere.
  const loadDistributionTag = useSetAtom(loadDistributionTagAtom);
  useEffect(() => {
    void loadDistributionTag();
  }, [loadDistributionTag]);

  // First-run dictate onboarding gate. Only the dictate-tagged bundle
  // (`isDictateBundle`, baked from NEXT_PUBLIC_DISTRIBUTION_TAG — never a
  // regular build) shows the no-sign-in onboarding at startup, and only until
  // the user has been through it once (the existing on-disk
  // `has_completed_dictation_onboarding` marker; `null` while that async read is
  // in flight). Auth is intentionally ignored: a leftover keychain token that
  // auto-signs-in must NOT skip the first run. The rollout flag
  // `IS_PLATFORM_DICTATION_ENABLED()` is also not consulted — it evaluates
  // against the anonymous (post-`posthog.reset()`) signed-out identity.
  const [hasCompletedDictateOnboarding, setHasCompletedDictateOnboarding] = useState<
    boolean | null
  >(isDictateBundle ? null : false);
  useEffect(() => {
    if (!isDictateBundle) return;
    let active = true;
    void hasCompletedDictationOnboarding()
      .then((completed) => {
        if (active) setHasCompletedDictateOnboarding(completed);
      })
      .catch(() => {
        // A failed read must not strand the gate on `null` — that would hold the
        // loader forever. Degrade to "not completed" so the dictate bundle still
        // shows its first-run onboarding rather than hanging.
        if (active) setHasCompletedDictateOnboarding(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // `true` while the first-run decision is unresolved (marker still loading) or
  // resolved to "not completed" — onboarding is/may be pending, so the
  // post-login redirect and the sign-in surface both stand down until it settles.
  const isDictateFirstRunPending =
    isDictateBundle && hasCompletedDictateOnboarding !== true;
  const shouldEnterDictateOnboarding =
    isDictateBundle && hasCompletedDictateOnboarding === false;

  // One navigator per page mount: it owns the "route change in flight" state,
  // so however many times the redirect effect below re-runs while the login
  // lands, only one `router.replace` is started (DSK-936).
  const bootNavigator = useMemo(
    () =>
      createBootNavigator({
        router,
        // If we do have to reload, land the `persist:root` write first so the
        // reloaded page rehydrates `authProvider` alongside the token. Bounded:
        // a stalled storage write must not hold the boot loader.
        beforeHardNavigation: () =>
          boundedPersistFlush(() => getPersistor().flush(), PERSIST_FLUSH_TIMEOUT_MS),
      }),
    [router]
  );
  useEffect(() => {
    // `appendAttributionParamsToRedirectUrl` carries both attribution
    // (utm_*, gclid, …) AND UI-state params (`?settings=<slug>`) through
    // the auth-bounce. The latter is load-bearing for the settings modal
    // external-link entry flow: without it, `/?settings=billing` →
    // `/scribe/new-user` drops the slug and the modal has no anchor to
    // reopen on. See DS-513.
    //
    // `.catch(() => {})` is the fire-and-forget pattern used elsewhere in
    // the codebase (e.g. `use-shared-template-preview-tracking.ts`).
    // Errors here would be Next router internals — non-actionable for the
    // user, and the loader UI below this effect masks any visible artifact.
    const redirectTarget = appendAttributionParamsToRedirectUrl(postLoginRedirectUrl);
    const { cleanup } = bootNavigator;
    const navigateAfterLogin = () => bootNavigator.navigateAfterLogin(redirectTarget);

    const redirectIfLoggedInTauri = async () => {
      // Hold the redirect until desktop connectivity resolves. Firing while
      // still `pending` is exactly the flash this ticket kills: the default
      // resolves to `/scribe/new-user` before a confirmed-offline reading can
      // flip it to `/scribe`, then a second push corrects it.
      if (isDesktopConnectivityPending) return;
      // Hold until the cached-identity read has answered. It is the positive
      // assertion the default is computed from, so firing before it resolves
      // would redirect on the not-yet-known case — the same "fire on a
      // provisional answer, then correct with a second push" flash.
      if (isDesktopIdentityPending) return;
      // Dictate first-run onboarding takes precedence: an auto-authenticated
      // dictate install must reach onboarding, not get bounced to /scribe.
      if (isDictateFirstRunPending) return;
      if (
        shouldRedirectDesktopTokenProvider({
          isAuth0Desktop,
          isKeycloak,
          token,
          desktopAccessToken,
        })
      ) {
        if (!token) {
          // Redirecting on the context session alone: nudge the auth cache's
          // deferred mirror-write (its own sanctioned Redux write) so the
          // token is in place for the app shell's first requests. On '/'
          // almost nothing else calls getAccessToken — which is exactly how
          // the deferral stranded this page. Fire-and-forget: a null result
          // leaves behavior identical to today.
          void import('@/lib/auth').then((m) => m.getAccessToken()).catch(() => {});
        }
        logger.debug(
          '(Desktop token provider) redirecting to scribe with postLoginRedirectUrl: ',
          postLoginRedirectUrl
        );
        await navigateAfterLogin();
        return;
      }
      const reactToken = await getToken?.();
      if (!!token && !!reactToken) {
        logger.debug('redirecting to scribe (Kinde)');
        await navigateAfterLogin();
      }
    };
    if (isTauri()) {
      void redirectIfLoggedInTauri();
    } else if (token) {
      void navigateAfterLogin();
    }

    return cleanup;
  }, [
    router,
    token,
    desktopAccessToken,
    getToken,
    postLoginRedirectUrl,
    isAuth0Desktop,
    isDictateFirstRunPending,
    isDesktopConnectivityPending,
    isDesktopIdentityPending,
    bootNavigator,
  ]);

  // Send first-run dictate-bundle users to the no-sign-in onboarding (gate
  // defined above). Users who have already completed it fall through to the
  // normal post-login / sign-in routing.
  useEffect(() => {
    if (!isTauri()) return;
    if (shouldEnterDictateOnboarding && !isAuthLoading) {
      void router.replace('/dictate-onboarding');
    }
  }, [router, isAuthLoading, shouldEnterDictateOnboarding]);

  useEffect(() => {
    // Skip for Auth0 Desktop - it has its own session management
    if (isAuth0Desktop) {
      return;
    }
    // Leftover token but auth provider reports logged out — clear stale state.
    // Guard: only clear if the JWT is actually expired. A non-expired token means
    // a fresh login just set it but isAuthenticated hasn't caught up yet (race
    // between Redux state and Kinde SDK hook updates). This synchronous check
    // eliminates the race window entirely.
    if (token && !isAuthenticated && !isAuthLoading) {
      if (!isJWTExpired(token)) {
        logger.info('[index] Skipping cleanup — token is not expired', {
          isAuthenticated,
          isAuthLoading,
        });
        return;
      }
      logger.info('[index] Clearing persisted models — token is expired', {
        hasToken: !!token,
        isAuthenticated,
        isAuthLoading,
        isAuth0Desktop,
        authProvider,
      });
      void clearPersistedModels();
    }
  }, [token, isAuthenticated, isAuthLoading, isAuth0Desktop, authProvider]);

  const pageTitle = intl.formatMessage(messages.pageTitle);
  const pageDescription = intl.formatMessage(messages.pageDescription);

  // Hoisted above the auth/flags gate so the SSR HTML (which crawlers index
  // for the "heidi login" SERP entry at scribe.heidihealth.com/) always
  // contains the login-specific title and description. `flagsReady` is read
  // from a Jotai atom that is `false` on the server because PostHog only
  // fetches flags client-side in a `useEffect`; gating the <Head> behind
  // `!flagsReady` would mean Googlebot's static-HTML pass saw only the
  // generic AppHead title and a thin-content loader body.
  const loginHead = (
    <Head>
      <title key="title">{pageTitle}</title>
      <meta key="description" name="description" content={pageDescription} />
      <meta key="og:title" property="og:title" content={pageTitle} />
      <meta key="og:description" property="og:description" content={pageDescription} />
      <meta key="twitter:title" name="twitter:title" content={pageTitle} />
      <meta
        key="twitter:description"
        name="twitter:description"
        content={pageDescription}
      />
    </Head>
  );

  // Hold the loader while a first-run dictate-onboarding redirect is pending —
  // including while the `has_completed` marker read is still in flight. `token ||`
  // short-circuits, so an authenticated launch always holds here until its
  // redirect fires.
  //
  // We deliberately do NOT gate this loader on desktop-connectivity `pending`:
  // for an authenticated user `token ||` already holds the loader (and the boot
  // redirect is separately deferred while pending — see the effect above), so the
  // term was redundant there; for a *signed-out* desktop user it only withheld the
  // sign-in screen behind a blank loader until the 8s connectivity fail-safe
  // elapsed on every cold launch — with no benefit, since a signed-out user has no
  // post-login redirect to defer. The offline-flash fix lives in the redirect
  // effect's `isDesktopConnectivityPending` guard above, not here.
  // On-prem desktop must always have a signed-out surface available locally.
  // PostHog flags are cloud-oriented here; if they ever fail to become ready,
  // hiding Keycloak sign-in behind that gate strands the Tauri shell on a loader.
  const shouldWaitForFlags = !isKeycloak && !flagsReady;
  if (token || shouldWaitForFlags || (isTauri() && isDictateFirstRunPending)) {
    return (
      <>
        {loginHead}
        <FullPageLoader />
      </>
    );
  }

  const legacySignIn = (
    <>
      {loginHead}
      <div className="flex h-dvh w-dvw flex-col bg-surface pt-[var(--app-titlebar-height,0px)]">
        <ForceUpdate />
        <ModalBlurOverlay />
        <SignInModal
          isOpen
          shouldBlurOverlay
          postLoginRedirectUri={postLoginRedirectUrl}
        />
      </div>
    </>
  );

  return legacySignIn;
};

export default Home;
